import { useEffect, useState } from "react";
import { useLoaderData } from "@remix-run/react";
import { VStack, StackDivider } from "@chakra-ui/react";

import {
  AvailabilityCalendar,
  FacilityHero,
  HowItWorksVideo,
  NotificationProof,
  PartnerCta,
  PhotoGallery,
  ReviewSection,
} from "#app/components/index.jsx";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { partnerLoader } from "#app/utils/partnerLoader.server.js";

export function meta({ data, matches }) {
  const baseUrl = getBaseUrl(matches);

  if (!data) {
    return getSeoMeta({
      title: "Campground not found | Schnerp",
      description: "The campground you're looking for could not be found.",
      image: getDefaultOgImage(baseUrl),
    });
  }

  return getSeoMeta({
    title: `${data.name} fully booked? Schnerp it! | Schnerp`,
    description: data.metaDescription,
    url: `${baseUrl}/${data.routeSlug}`,
    image: getDefaultOgImage(baseUrl),
  });
}

export const loader = partnerLoader;

export default function PartnerFacilityPage() {
  const data = useLoaderData();
  // Multi-facility partners render the calendar with a facility tab strip; the
  // active tab lives here so the calendar can swap facilities. Single-facility
  // partners ignore it.
  const [activeIndex, setActiveIndex] = useState(0);
  // All partner slugs render this same component, so client-side navigation
  // between two partners preserves activeIndex. Reset it when the loaded partner
  // changes so a tab selected on one multi-facility partner can't point past the
  // next partner's (possibly shorter) facility list.
  useEffect(() => setActiveIndex(0), [data.routeSlug]);
  const active = data.facilities ? data.facilities[activeIndex] : null;
  const newPath = `/new/${active?.slug ?? data.facilitySlug}`;

  return (
    <VStack spacing={{ base: 10, md: 16 }} divider={<StackDivider />}>
      <FacilityHero
        name={data.name}
        subtitle={data.subtitle}
        reviews={data.reviews}
        stats={data.stats}
        freeAccessOffer={data.freeAccessOffer}
      />
      <AvailabilityCalendar
        facilityId={active?.id ?? data.facilityId}
        facilitySlug={active?.slug ?? data.facilitySlug}
        facilityName={active?.name ?? data.name}
        facilities={data.facilities ?? undefined}
        activeFacilityIndex={activeIndex}
        onFacilityChange={setActiveIndex}
        siteGroups={data.siteGroups ?? undefined}
      />
      <HowItWorksVideo />
      <NotificationProof
        openings={data.recentOpenings}
        alertsSent={data.alertsSent}
        ctaTo={newPath}
      />
      {data.photos?.length > 0 && (
        <PhotoGallery photos={data.photos} facilityName={data.name} />
      )}
      <ReviewSection reviews={data.reviews} stats={data.stats} />
      {/* Partner pages close with the facility-specific PartnerCta card — its
          copy ("Start watching {partner}") and any sponsored offer pill fit a
          single partner better than the generic free-to-try card. `ctaTo`
          defaults to "#calendar". */}
      <PartnerCta
        name={data.name}
        body={data.closingCtaBody}
        freeAccessOffer={data.freeAccessOffer}
      />
    </VStack>
  );
}
